import { HttpParams } from '@angular/common/http'
import { Component, OnDestroy, ViewChild, effect, input } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { Globalize } from '@grapecity/wijmo'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { CellEditEndingEventArgs, CellType, Column, DataMap, FlexGrid, GridPanel } from '@grapecity/wijmo.grid'
import { Subscription } from 'rxjs'
import { HrGoalComponent } from 'src/app/hr/scorecard/modals/hr-goal.component'
import { MeetingEntityTypes } from 'src/app/shared/services/meeting-hub.service'
import { getInitials } from 'src/app/shared/shared-functions'

import { BaseComponent, FeatureType, Grid, HrGoal, ListItem, Meeting, ModalBackDrop, ModalSize, VpUser } from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { GridSelectionCalculationComponent } from '../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { MeetingItemsSelectionComponent } from '../meeting-items-selection/meeting-items-selection.component'
import { MeetingPickerModalComponent } from '../meeting-picker-modal/meeting-picker-modal.component'
import { GoalsExportModalComponent, GoalsExportRange } from './goals-export-modal.component'

@Component({
    selector: 'app-goals',
    templateUrl: './goals.component.html',
    standalone: true,
    imports: [
        FormsModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        GridExportComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
        GridSelectionCalculationComponent,
    ],
})
export class GoalsComponent extends BaseComponent implements OnDestroy {
    faSearch = faSearch

    meetingId = input.required<string>()
    allUsers: VpUser[] = []
    allUserMap!: DataMap

    weekCols = [] as string[]
    /** Start date of each week column (same order as weekCols), used for date-range export. */
    weekColStartDates: Date[] = []

    canViewAdmin = true

    private contentSyncSub?: Subscription
    private isCellEditing = false
    private pendingContentSync = false

    @ViewChild(GridExportComponent) gridExportComponent!: GridExportComponent

    grid: Grid<HrGoal>
    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.MeetingsMeetingEdit])
    }

    protected goalDirectionMap = new DataMap(
        [new ListItem(0, '>='), new ListItem(1, '<='), new ListItem(2, '>= <='), new ListItem(3, '=')],
        'value',
        'display',
    )

    constructor() {
        super()
        this.grid = new Grid<HrGoal>(this.services)
            .setupModalEditing(this.services.data.hrGoals, 'Metric', HrGoalComponent, () => new HrGoal(), this, ModalSize.medium)
            .filterBy(item => [item.name])
            .isReadOnly(false)
            .wrapColumnHeaders()
            .editOnDoubleClick(false)
        //.allowDragging(AllowDragging.Both) // Customer wanted custom sorting instead of reordering

        // const reordered = () => {
        //     this.gridReordered()
        // }
        // this.grid.dragged.on(reordered)

        effect(() => {
            if (this.meetingId()) {
                this.refreshGoals()
                console.log(this.meetingId())
            }
        })

        this.contentSyncSub = this.services.meetingContentSync.subscribe({
            meetingId: () => this.meetingId(),
            entityTypes: [MeetingEntityTypes.HrGoal],
            isEditing: () => this.isCellEditing,
            onRefresh: () => this.refreshGoals(true),
            onDeferredRefresh: () => {
                this.pendingContentSync = true
            },
        })
    }

    ngOnDestroy(): void {
        this.contentSyncSub?.unsubscribe()
    }

    refreshGoals(silent = false) {
        const params = new HttpParams().set('meetingId', this.meetingId())
        this.cancelableRequest(
            'GoalsRefresh',
            !silent,
            this.services.data
                .load(
                    !silent,
                    !silent,
                    this.services.data.hrGoals.queryAllByAction('GetByMeetingId', params),
                    this.services.data.get('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', true)),
                )
                .subscribe({
                    next: result => {
                        this.initialLoadingComplete()
                        this.grid.items = result[0]
                        this.allUsers = result[1]
                        this.allUserMap = new DataMap(this.allUsers, 'id', 'name')

                        this.generateColumns()
                    },
                }),
        )
    }

    generateColumns() {
        this.weekCols = []
        this.weekColStartDates = []
        setTimeout(() => {
            const uniqueWeekValues = new Set<number>()

            // Collect all weekValues from all records
            for (const item of this.grid.items) {
                if (item.weeklyOaValues) {
                    for (const prop in item.weeklyOaValues) {
                        uniqueWeekValues.add(+prop)
                    }
                }
            }

            // Convert Set to array and sort if needed
            const sortedWeekValues = Array.from(uniqueWeekValues).orderByDescending(x => x)
            const weekDates = sortedWeekValues.map(x => this.fromOADate(x))
            this.weekColStartDates = weekDates
            this.weekCols = weekDates.map(x => this.formatDateRange(x))
        }, 1)
    }

    // gridReordered() {
    //     const goals = this.grid.view.items.slice()
    //     this.grid.items = this.grid.view.items.slice() as any
    //     const params = new HttpParams().set('meetingId', this.meetingId())
    //     console.log('Goals reordered 1')
    //     this.cancelableRequest(
    //         'reorderGoal',
    //         true,
    //         this.services.data.put('/api/Meeting/BulkUpdateMeetingGoalOrder', goals, params).subscribe(),
    //     )
    // }

    goalsGridItemFormatter = (panel: GridPanel, r, c, cell: HTMLDivElement) => {
        if (panel.cellType === CellType.Cell) {
            const col = panel.grid.columns[c]
            const row = panel.rows[r]
            const item = row.dataItem as HrGoal
            cell.classList.remove('red-cell')
            cell.classList.remove('green-cell')

            if (panel.grid.activeEditor) console.log(panel.grid.activeEditor)

            if (col.binding === 'goal1') {
                if (item.goalDirection === 2) {
                    cell.innerHTML = Globalize.format(item.goal1, item.format) + ' - ' + Globalize.format(item.goal2, item.format)
                } else {
                    cell.innerHTML = Globalize.format(item.goal1, item.format)
                }
            }
            if (col.binding === 'userId') {
                const originalVal = cell.innerHTML
                cell.title = originalVal
                cell.innerHTML = getInitials(originalVal)
            }

            if (col.binding === 'average') {
                cell.innerHTML = Globalize.format(item.average, item.format)
                if (!item.showAverage) cell.innerHTML = ''
            }

            if (col.binding === 'sum') {
                cell.innerHTML = Globalize.format(item.sum, item.format)

                // Don't show the sum for percents, since it doesn't really make sense.
                if (item.format[0].toUpperCase() === 'P' || !item.showSum) cell.innerHTML = ''
            }

            if (c > 5) {
                if (!panel.grid.activeEditor) {
                    // apply the format
                    const originalVal = item.weekValues[col.binding!.split('.')[1]]
                    cell.innerHTML = Globalize.format(originalVal, item.format)

                    // apply the goal meeting color
                    if (originalVal != null) {
                        switch (item.goalDirection) {
                            case 0:
                                if (originalVal >= item.goal1) cell.classList.add('green-cell')
                                else cell.classList.add('red-cell')
                                break
                            case 1:
                                if (originalVal <= item.goal1) cell.classList.add('green-cell')
                                else cell.classList.add('red-cell')
                                break
                            case 2:
                                if (originalVal >= item.goal1 && originalVal <= item.goal2) cell.classList.add('green-cell')
                                else cell.classList.add('red-cell')
                                break
                            case 3:
                                if (originalVal === item.goal1) cell.classList.add('green-cell')
                                else cell.classList.add('red-cell')
                                break
                            default:
                        }
                    }
                }
            }
        }
    }

    beginningEdit(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        this.isCellEditing = true
        // if (!this.canEditGoals) args.cancel = true
        // if (this.canEditAdmin) args.cancel = false

        // Don't allow the first date column to be editing if it 'IsCalculated'
        if (flexGrid.rows[args.row].dataItem.isCalculated && args.col === 6) {
            args.cancel = true
        }

        // Don't allow other date columns to be edited if the row isn't marked 'CanEditHistorical'
        if (!flexGrid.rows[args.row].dataItem.canEditHistorical && args.col > 6) {
            args.cancel = true
        }
    }

    cellEditEnded(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        if (args.cancel) {
            this.isCellEditing = false
            return
        }
        const item = flexGrid.rows[args.row].dataItem
        const key = flexGrid.columns[args.col].binding?.split('.')[1] as string

        item.weekValues[key] = item.weekValues[key] === null ? null : +item.weekValues[key]

        this.bulkUpdateGoalValues([
            {
                id: item.id,
                date: this.getDateFromBinding(flexGrid.columns[args.col].binding),
                value: item.weekValues[key],
            },
        ])
        this.isCellEditing = false
        if (this.pendingContentSync) {
            this.pendingContentSync = false
            this.refreshGoals(true)
        }
    }

    pasting(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        if (args.cancel) return
        args.cancel = true
        this.services.notification.info('Pasting is not yet supported.')
        // TODO: support pasting and pasted. An example is in the materials.component.ts file.
    }

    bulkUpdateGoalValues(values: any[]) {
        this.services.data
            .put('api/HrGoal/SaveGoalWeeks', values)
            .subscribe({ error: () => this.services.notification.error('The metric changes were not successfully saved') })
    }

    getDateFromBinding(binding: string | null) {
        // The binding can be split and parsed to get the date,
        // but last date has the year, so we start with that.
        const parts = binding?.split(' ') as string[]
        const month = parts[2]
        const day = +parts[3]
        const year = 2000 + +parts[4].replace("'", '')
        const endDate = new Date(year, this.monthMap[month], day)
        const startDate = endDate.addDays(-6)
        return startDate
    }

    showExportModal() {
        const minDate = this.weekColStartDates.length ? this.weekColStartDates[this.weekColStartDates.length - 1]! : new Date()
        const maxDate = this.weekColStartDates.length ? this.weekColStartDates[0]! : new Date()
        const ref = this.services.modals.open(GoalsExportModalComponent, ModalSize.medium, ModalBackDrop.darkAndActive)
        const instance = ref.componentInstance as GoalsExportModalComponent
        instance.minDate = minDate
        instance.maxDate = maxDate
        instance.startDate = new Date(minDate.getTime())
        instance.endDate = new Date(maxDate.getTime())
        ref.result.then(
            (result: GoalsExportRange | null) => {
                if (result == null) {
                    this.exportWithDateRange(null, null) // all columns
                } else {
                    this.exportWithDateRange(result.startDate, result.endDate)
                }
            },
            () => {
                /* user dismissed */
            },
        )
    }

    /**
     * Build a temporary FlexGrid with only the desired columns (Who through Sum + week columns), then export and dispose.
     * When startDate/endDate are null, all week columns are included; otherwise only weeks in that range are included.
     */
    exportWithDateRange(startDate: Date | null, endDate: Date | null) {
        const weekColsToExport =
            startDate != null && endDate != null
                ? this.weekCols.filter((_, i) => {
                      const d = this.weekColStartDates[i]
                      if (!d) return false
                      const t = d.getTime()
                      return t >= startDate.getTime() && t <= endDate.getTime()
                  })
                : [...this.weekCols]

        const host = document.createElement('div')
        host.style.position = 'absolute'
        host.style.left = '-9999px'
        document.body.appendChild(host)
        const tempGrid = new FlexGrid(host)
        tempGrid.autoGenerateColumns = false
        tempGrid.columns.push(new Column({ binding: 'userId', header: 'Who', width: 75, dataMap: this.allUserMap }))
        tempGrid.columns.push(new Column({ binding: 'name', header: 'Metric', width: 300 }))
        tempGrid.columns.push(
            new Column({
                binding: 'goalDirection',
                header: ' ',
                width: 70,
                dataMap: this.goalDirectionMap,
                align: 'center',
            }),
        )
        tempGrid.columns.push(new Column({ binding: 'goal1', header: 'Goal', width: 120, align: 'center' }))
        tempGrid.columns.push(new Column({ binding: 'average', header: 'Average', width: 100, align: 'center' }))
        tempGrid.columns.push(new Column({ binding: 'sum', header: 'Sum', width: 120, align: 'center' }))
        for (const weekCol of weekColsToExport) {
            tempGrid.columns.push(new Column({ binding: 'weekValues.' + weekCol, header: weekCol, width: 100, align: 'center' }))
        }
        tempGrid.itemsSource = this.grid.items
        tempGrid.itemFormatter = (panel: GridPanel, r: number, c: number, cell: HTMLElement) =>
            this.goalsGridItemFormatter(panel, r, c, cell as HTMLDivElement)
        const onComplete = () => {
            tempGrid.dispose()
            host.remove()
        }
        this.gridExportComponent.export(tempGrid, onComplete)
    }

    showSelectionModal() {
        const ref = this.services.modals.open(MeetingItemsSelectionComponent, ModalSize.medium, ModalBackDrop.darkAndActive)
        const instance = ref.componentInstance as MeetingItemsSelectionComponent
        instance.itemType = 'Goals'
        instance.meetingId = this.meetingId()
        instance.itemIds = this.grid.items.map(x => x.id)
        ref.result.then(
            () => {
                this.refreshGoals()
            },
            () => {
                console.log('dismissed')
            },
        )
    }

    showCopyFromMeetingModal() {
        this.services.data.meetings.queryAll().subscribe({
            next: meetings => {
                const ref = this.services.modals.open(MeetingPickerModalComponent, ModalSize.medium, ModalBackDrop.darkAndActive)
                const instance = ref.componentInstance as MeetingPickerModalComponent
                instance.meetings = (meetings as Meeting[]).filter(m => m.id !== this.meetingId()).orderBy(m => m.name)
                ref.result.then(
                    (sourceMeetingId: string) => {
                        if (sourceMeetingId) this.copyMetricsFromMeeting(sourceMeetingId)
                    },
                    () => {
                        // dismissed
                    },
                )
            },
        })
    }

    private copyMetricsFromMeeting(sourceMeetingId: string) {
        const params = new HttpParams().set('meetingId', sourceMeetingId)
        this.services.data.hrGoals.queryAllByAction('GetByMeetingId', params).subscribe({
            next: (sourceGoals: HrGoal[]) => {
                // Merge (union) the source meeting's metrics with the ones already in this meeting so the
                // sync-style UpdateItemIds call does not drop existing metrics.
                const mergedIds = Array.from(new Set([...this.grid.items.map(x => x.id), ...sourceGoals.map(x => x.id)]))
                const putParams = new HttpParams().set('itemType', 'Goals').set('meetingId', this.meetingId()).set('isAdding', true)
                this.services.data.put('/api/Meeting/UpdateItemIds', mergedIds, putParams).subscribe({
                    next: () => {
                        this.services.notification.success('Metrics added from the selected meeting')
                        this.refreshGoals()
                    },
                    error: error => this.services.notification.error('Failed to add metrics: ' + error.message),
                })
            },
        })
    }

    removeSelections() {
        const items = [] as HrGoal[]
        for (const item of this.grid.flexGrid.selectedRows) {
            items.push(item.dataItem)
        }
        const ids = items.map(x => x.id)
        const params = new HttpParams().set('itemType', 'Goals').set('meetingId', this.meetingId()).set('isAdding', false)
        this.services.data.put('/api/Meeting/UpdateItemIds', ids, params).subscribe({
            next: () => {
                this.refreshGoals()
            },
        })
    }

    fromOADate(oaDate: number): Date {
        const OLE_AUTOMATION_EPOCH = new Date(Date.UTC(1899, 11, 30)) // 1899-12-30
        const days = Math.floor(oaDate)
        const msPerDay = 24 * 60 * 60 * 1000
        const msFromDays = days * msPerDay

        // fractional part of a day → ms
        const fraction = oaDate - days
        const msFromFraction = fraction * msPerDay

        return new Date(OLE_AUTOMATION_EPOCH.getTime() + msFromDays + msFromFraction).addDays(1)
    }

    formatDateRange(date: Date): string {
        const options: Intl.DateTimeFormatOptions = { month: 'short', day: '2-digit' }

        // format starting date
        const start = date.toLocaleDateString('en-US', options)

        // add 6 days to get the end of the week (Sunday + 6 = Saturday)
        const nextWeek = new Date(date)
        nextWeek.setDate(nextWeek.getDate() + 6)
        const end = nextWeek.toLocaleDateString('en-US', options)

        // get last two digits of year from the END date (to match backend behavior)
        // This ensures weeks spanning year boundaries show the correct year (e.g., Dec 29 - Jan 03 '26)
        const year = `'${nextWeek.getFullYear().toString().slice(-2)}`

        return `${start} ${end} ${year}`
    }

    private monthMap: Record<string, number> = {
        Jan: 0,
        Feb: 1,
        Mar: 2,
        Apr: 3,
        May: 4,
        Jun: 5,
        Jul: 6,
        Aug: 7,
        Sep: 8,
        Oct: 9,
        Nov: 10,
        Dec: 11,
    }
}
